import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { createPersistedMockData } from '../mockDataManager';
import {
  fetchTabConfigurations,
  fetchTabConfiguration,
  createTabConfiguration as createTabConfigGobodo,
  updateTabConfiguration as updateTabConfigGobodo,
  deleteTabConfiguration as deleteTabConfigGobodo,
  objectNameToApiName,
} from '../gobodo/gobodoTabConfigService';

// ============================================
// TYPES
// ============================================

export interface TabConfigSection {
  id: string;
  type: 'form' | 'table' | 'widget' | 'related-list';
  name: string;
  label: string;
  order: number;
  config: any; // Type-specific configuration
}

export interface TabConfig {
  id: string;
  label: string;
  icon?: string;
  order: number;
  sections: TabConfigSection[];
}

export interface RecordPageConfig {
  id: string;
  objectId: string;
  name: string;
  description?: string;
  applicationName?: string; // Which app uses this config (global, cybersweep, credit-repair, id-theft, credit-development, credit-block, all)
  tabs: TabConfig[];
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

// ============================================
// TAB CONFIGURATION TYPES (CHUNK 40)
// Controls which tabs are visible/enabled per application
// ============================================

export interface ConfiguredTab {
  id: string;
  label: string;
  isVisible: boolean;
  isDefault: boolean;
  order: number;
  source: 'standard' | 'custom' | 'related';
  relatedObject?: string;
}

export interface TabConfiguration {
  id: string;
  objectName: string;
  application?: string; // undefined = All Applications
  tabs: ConfiguredTab[];
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

// ============================================
// MOCK DATA
// ============================================

const mockRecordPages: Record<string, RecordPageConfig[]> = {
  obj_client: [
    {
      id: 'recordpage_001',
      objectId: 'obj_client',
      name: 'CyberSweep Client Record Page',
      description: 'Tab configuration for CyberSweep application',
      applicationName: 'cybersweep',
      tabs: [
        {
          id: 'tab_001',
          label: 'Client Details',
          icon: 'User',
          order: 1,
          sections: [
            {
              id: 'section_001',
              type: 'form',
              name: 'basic_information',
              label: 'Basic Information',
              order: 1,
              config: { fields: ['name', 'email', 'phone'] }
            },
            {
              id: 'section_002',
              type: 'form',
              name: 'cybersweep_dates',
              label: 'CyberSweep Dates',
              order: 2,
              config: { fields: ['cybersweep_start_date__c', 'cybersweep_end_date__c'] }
            }
          ]
        },
        {
          id: 'tab_002',
          label: 'Classic Audit',
          icon: 'FileText',
          order: 2,
          sections: [
            {
              id: 'section_003',
              type: 'widget',
              name: 'gmail_account',
              label: 'Gmail Account',
              order: 1,
              config: { componentName: 'GmailAccountWidget' }
            },
            {
              id: 'section_004',
              type: 'table',
              name: 'cs_audits',
              label: 'CS Audits',
              order: 2,
              config: { dataSource: 'cs-audits', columns: ['auditId', 'auditType', 'auditor'] }
            }
          ]
        },
        {
          id: 'tab_003',
          label: 'Automated Audit',
          icon: 'Zap',
          order: 3,
          sections: []
        },
        {
          id: 'tab_004',
          label: 'Configuration',
          icon: 'Settings',
          order: 4,
          sections: [
            {
              id: 'section_005',
              type: 'widget',
              name: 'pii_monitoring',
              label: 'PII Monitoring',
              order: 1,
              config: { componentName: 'PIIMonitoringWidget' }
            }
          ]
        }
      ],
      createdBy: 'Admin',
      createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'recordpage_002',
      objectId: 'obj_client',
      name: 'Credit Repair Client Record Page',
      description: 'Tab configuration for Credit Repair application',
      applicationName: 'credit-repair',
      tabs: [
        {
          id: 'tab_005',
          label: 'Client Overview',
          icon: 'User',
          order: 1,
          sections: [
            {
              id: 'section_006',
              type: 'form',
              name: 'client_info',
              label: 'Client Information',
              order: 1,
              config: { fields: ['name', 'email', 'annual_contract_value__c'] }
            }
          ]
        },
        {
          id: 'tab_006',
          label: 'Credit History',
          icon: 'FileText',
          order: 2,
          sections: [
            {
              id: 'section_007',
              type: 'table',
              name: 'credit_events',
              label: 'Credit Events',
              order: 1,
              config: { dataSource: 'credit-events', columns: ['date', 'type', 'amount'] }
            }
          ]
        },
        {
          id: 'tab_007',
          label: 'Documents',
          icon: 'Files',
          order: 3,
          sections: []
        }
      ],
      createdBy: 'Admin',
      createdDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'recordpage_003',
      objectId: 'obj_client',
      name: 'Standard Client Record Page',
      description: 'Default tab configuration',
      applicationName: 'all',
      tabs: [
        {
          id: 'tab_008',
          label: 'Details',
          icon: 'FileText',
          order: 1,
          sections: [
            {
              id: 'section_008',
              type: 'form',
              name: 'information',
              label: 'Information',
              order: 1,
              config: { fields: ['name', 'email', 'phone'] }
            }
          ]
        },
        {
          id: 'tab_009',
          label: 'Related',
          icon: 'Link',
          order: 2,
          sections: [
            {
              id: 'section_009',
              type: 'related-list',
              name: 'related_records',
              label: 'Related Records',
              order: 1,
              config: { relatedObject: 'related_clients' }
            }
          ]
        }
      ],
      createdBy: 'System',
      createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
};

// Tab Configuration Mock Data (CHUNK 40)
const defaultTabConfigurations: TabConfiguration[] = [
  {
    id: 'tabconfig_001',
    objectName: normalizeToGobodoFormat('Client'),
    application: 'cybersweep',
    tabs: [
      { id: 'tab_001', label: 'Details', isVisible: true, isDefault: true, order: 1, source: 'standard' },
      { id: 'tab_002', label: 'Related Contacts', isVisible: true, isDefault: false, order: 2, source: 'standard' },
      { id: 'tab_003', label: 'Activity', isVisible: true, isDefault: false, order: 3, source: 'standard' },
      { id: 'tab_004', label: 'Files', isVisible: false, isDefault: false, order: 4, source: 'standard' },
      { id: 'tab_005', label: 'CyberSweep Audits', isVisible: true, isDefault: false, order: 5, source: 'custom' }
    ],
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  },
  {
    id: 'tabconfig_002',
    objectName: normalizeToGobodoFormat('Client'),
    application: 'credit-repair',
    tabs: [
      { id: 'tab_001', label: 'Details', isVisible: true, isDefault: true, order: 1, source: 'standard' },
      { id: 'tab_002', label: 'Related Contacts', isVisible: true, isDefault: false, order: 2, source: 'standard' },
      { id: 'tab_003', label: 'Activity', isVisible: false, isDefault: false, order: 3, source: 'standard' },
      { id: 'tab_004', label: 'Files', isVisible: true, isDefault: false, order: 4, source: 'standard' },
      { id: 'tab_006', label: 'Credit Reports', isVisible: true, isDefault: false, order: 5, source: 'custom' },
      { id: 'tab_007', label: 'Dispute Letters', isVisible: true, isDefault: false, order: 6, source: 'custom' }
    ],
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  },
  {
    id: 'tabconfig_003',
    objectName: normalizeToGobodoFormat('Client'),
    application: undefined, // All Applications
    tabs: [
      { id: 'tab_001', label: 'Details', isVisible: true, isDefault: true, order: 1, source: 'standard' },
      { id: 'tab_002', label: 'Related Contacts', isVisible: true, isDefault: false, order: 2, source: 'standard' },
      { id: 'tab_003', label: 'Activity', isVisible: true, isDefault: false, order: 3, source: 'standard' },
      { id: 'tab_004', label: 'Files', isVisible: true, isDefault: false, order: 4, source: 'standard' }
    ],
    createdBy: 'System',
    createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'System'
  },
  {
    id: 'tabconfig_004',
    objectName: normalizeToGobodoFormat('Contact'),
    application: undefined,
    tabs: [
      { id: 'tab_001', label: 'Information', isVisible: true, isDefault: true, order: 1, source: 'standard' },
      { id: 'tab_002', label: 'Related Accounts', isVisible: true, isDefault: false, order: 2, source: 'related', relatedObject: normalizeToGobodoFormat('Account') },
      { id: 'tab_003', label: 'Communication History', isVisible: true, isDefault: false, order: 3, source: 'standard' }
    ],
    createdBy: 'System',
    createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'System'
  }
];

const persistedTabConfigurations = createPersistedMockData<TabConfiguration>(
  'crm_tab_configurations',
  defaultTabConfigurations
);

// ============================================
// QUERY KEYS
// ============================================

export const recordPageKeys = {
  all: ['record-pages'] as const,
  byObject: (objectId: string) => [...recordPageKeys.all, 'object', objectId] as const,
  detail: (recordPageId: string) => [...recordPageKeys.all, 'detail', recordPageId] as const,
};

export const tabConfigKeys = {
  all: ['tab-configurations'] as const,
  byObject: (objectName: string) => [...tabConfigKeys.all, 'object', objectName] as const,
  detail: (configId: string) => [...tabConfigKeys.all, 'detail', configId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all record page configurations for an object
 * Future: Replace with API call to GET /api/objects/:objectId/record-pages
 */
export function useRecordPagesByObject(objectId: string, filters?: {
  applicationName?: string;
}) {
  return useQuery({
    queryKey: [...recordPageKeys.byObject(objectId), JSON.stringify(filters || {})],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));

      let recordPages = mockRecordPages[objectId] || [];

      // Apply filters
      if (filters?.applicationName && filters.applicationName !== 'all') {
        recordPages = recordPages.filter(rp =>
          rp.applicationName === filters.applicationName || rp.applicationName === 'all'
        );
      }

      // Sort: alphabetically by name
      return recordPages.sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!objectId,
  });
}

/**
 * Get single record page configuration
 * Future: Replace with API call to GET /api/record-pages/:recordPageId
 */
export function useRecordPage(recordPageId: string) {
  return useQuery({
    queryKey: recordPageKeys.detail(recordPageId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      // Find record page across all objects
      for (const recordPages of Object.values(mockRecordPages)) {
        const recordPage = recordPages.find(rp => rp.id === recordPageId);
        if (recordPage) return recordPage;
      }

      throw new Error('Record page not found');
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!recordPageId,
  });
}

/**
 * Create new record page configuration
 * Future: Replace with API call to POST /api/objects/:objectId/record-pages
 */
export function useCreateRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordPageData: Omit<RecordPageConfig, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>) => {
      await new Promise(resolve => setTimeout(resolve, 600));

      const newRecordPage: RecordPageConfig = {
        ...recordPageData,
        id: `recordpage_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        tabs: recordPageData.tabs || []
      };

      // Add to mock data
      if (!mockRecordPages[recordPageData.objectId]) {
        mockRecordPages[recordPageData.objectId] = [];
      }
      mockRecordPages[recordPageData.objectId].push(newRecordPage);

      return newRecordPage;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update record page configuration
 * Future: Replace with API call to PATCH /api/record-pages/:recordPageId
 */
export function useUpdateRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordPageId, updates }: { recordPageId: string; updates: Partial<RecordPageConfig> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      // Find and update record page
      for (const objectId in mockRecordPages) {
        const index = mockRecordPages[objectId].findIndex(rp => rp.id === recordPageId);
        if (index !== -1) {
          mockRecordPages[objectId][index] = {
            ...mockRecordPages[objectId][index],
            ...updates,
            lastModified: new Date().toISOString()
          };
          return mockRecordPages[objectId][index];
        }
      }

      throw new Error('Record page not found');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: recordPageKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete record page configuration
 * Future: Replace with API call to DELETE /api/record-pages/:recordPageId
 */
export function useDeleteRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordPageId: string) => {
      await new Promise(resolve => setTimeout(resolve, 400));

      // Find and remove record page
      for (const objectId in mockRecordPages) {
        const recordPage = mockRecordPages[objectId].find(rp => rp.id === recordPageId);
        if (recordPage) {
          mockRecordPages[objectId] = mockRecordPages[objectId].filter(rp => rp.id !== recordPageId);
          return { recordPageId, objectId };
        }
      }

      throw new Error('Record page not found');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone record page configuration
 * Future: Replace with API call to POST /api/record-pages/:recordPageId/clone
 */
export function useCloneRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordPageId: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      // Find record page to clone
      for (const objectId in mockRecordPages) {
        const recordPage = mockRecordPages[objectId].find(rp => rp.id === recordPageId);
        if (recordPage) {
          const clonedRecordPage: RecordPageConfig = {
            ...recordPage,
            id: `recordpage_${Date.now()}`,
            name: `${recordPage.name} (Copy)`,
            createdBy: 'Current User',
            createdDate: new Date().toISOString(),
            lastModified: new Date().toISOString()
          };

          mockRecordPages[objectId].push(clonedRecordPage);
          return clonedRecordPage;
        }
      }

      throw new Error('Record page not found');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectId) });
    },
  });
}

// ============================================
// TAB CONFIGURATION HOOKS (CHUNK 40)
// ============================================

function matchesObject(tc: TabConfiguration, objectName: string): boolean {
  const apiName = objectNameToApiName(objectName);
  return objectNameToApiName(tc.objectName) === apiName;
}

/**
 * Get all tab configurations for an object
 */
export function useTabConfigurations(objectName: string, filters?: {
  application?: string;
}) {
  return useQuery({
    queryKey: [...tabConfigKeys.byObject(objectName), JSON.stringify(filters || {})],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        const apiName = objectNameToApiName(objectName);
        const configs = await fetchTabConfigurations(apiName, filters?.application);
        const withObjectName = configs.map(c => ({ ...c, objectName }));
        return withObjectName.sort((a, b) => {
          if (a.application && !b.application) return -1;
          if (!a.application && b.application) return 1;
          return (a.application || '').localeCompare(b.application || '');
        });
      }

      let configs = persistedTabConfigurations.get().filter(tc => matchesObject(tc, objectName));
      if (filters?.application && filters.application !== 'all') {
        configs = configs.filter(tc =>
          tc.application === filters.application || tc.application === undefined
        );
      }
      return configs.sort((a, b) => {
        if (a.application && !b.application) return -1;
        if (!a.application && b.application) return 1;
        return (a.application || '').localeCompare(b.application || '');
      });
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!objectName,
  });
}

/**
 * Get single tab configuration
 */
export function useTabConfiguration(configId: string) {
  return useQuery({
    queryKey: tabConfigKeys.detail(configId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchTabConfiguration(configId);
      }
      const config = persistedTabConfigurations.get().find(tc => tc.id === configId);
      if (!config) throw new Error('Tab configuration not found');
      return config;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!configId,
  });
}

/**
 * Create new tab configuration
 */
export function useCreateTabConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configData: Omit<TabConfiguration, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>) => {
      if (USE_GOBODO_API) {
        const created = await createTabConfigGobodo({
          ...configData,
          createdBy: 'Current User',
          lastModifiedBy: 'Current User',
        });
        return { ...created, objectName: configData.objectName };
      }
      const newConfig: TabConfiguration = {
        ...configData,
        id: `tabconfig_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      return persistedTabConfigurations.add(newConfig);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tabConfigKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update tab configuration
 */
export function useUpdateTabConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, updates }: { configId: string; updates: Partial<TabConfiguration> }) => {
      if (USE_GOBODO_API) {
        return updateTabConfigGobodo(configId, {
          tabs: updates.tabs,
          application: updates.application,
          objectName: updates.objectName,
        });
      }
      const updated = persistedTabConfigurations.update(configId, {
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      });
      if (!updated) throw new Error('Tab configuration not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tabConfigKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: tabConfigKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete tab configuration
 */
export function useDeleteTabConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, objectName }: { configId: string; objectName: string }) => {
      if (USE_GOBODO_API) {
        await deleteTabConfigGobodo(configId);
        return { configId, objectName };
      }
      if (!persistedTabConfigurations.remove(configId)) {
        throw new Error('Tab configuration not found');
      }
      return { configId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tabConfigKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone tab configuration
 */
export function useCloneTabConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configId: string) => {
      if (USE_GOBODO_API) {
        const config = await fetchTabConfiguration(configId);
        return createTabConfigGobodo({
          objectName: config.objectName,
          application: config.application ? `${config.application} (Copy)` : undefined,
          tabs: config.tabs,
          createdBy: 'Current User',
          lastModifiedBy: 'Current User',
        });
      }
      const config = persistedTabConfigurations.get().find(tc => tc.id === configId);
      if (!config) throw new Error('Tab configuration not found');

      const clonedConfig: TabConfiguration = {
        ...config,
        id: `tabconfig_${Date.now()}`,
        application: config.application ? `${config.application} (Copy)` : undefined,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      return persistedTabConfigurations.add(clonedConfig);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tabConfigKeys.byObject(data.objectName) });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get application name display label
 */
export function getApplicationLabel(appName?: string): string {
  const labels: Record<string, string> = {
    global: 'Global/Shared',
    cybersweep: 'CyberSweep',
    'credit-repair': 'Credit Repair',
    'id-theft': 'ID Theft Recovery',
    'credit-development': 'Credit Development',
    'credit-block': 'Credit Block',
    all: 'All Applications'
  };
  return labels[appName || 'all'] || appName || 'All Applications';
}

/**
 * Count total tabs in record page
 */
export function getTotalTabCount(recordPage: RecordPageConfig): number {
  return recordPage.tabs.length;
}

/**
 * Count total sections across all tabs
 */
export function getTotalSectionCount(recordPage: RecordPageConfig): number {
  return recordPage.tabs.reduce((total, tab) => total + tab.sections.length, 0);
}

/**
 * Get section type display label
 */
export function getSectionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    form: 'Form',
    table: 'Table',
    widget: 'Widget',
    'related-list': 'Related List'
  };
  return labels[type] || type;
}

/**
 * Count visible tabs in a tab configuration
 */
export function countVisibleTabs(config: TabConfiguration): number {
  return config.tabs.filter(tab => tab.isVisible).length;
}

/**
 * Get the default tab from a tab configuration
 */
export function getDefaultTab(config: TabConfiguration): ConfiguredTab | undefined {
  return config.tabs.find(tab => tab.isDefault);
}

/**
 * Get standard tabs that are available for all objects
 */
export function getStandardTabs(): Omit<ConfiguredTab, 'isVisible' | 'isDefault' | 'order'>[] {
  return [
    { id: 'details', label: 'Details', source: 'standard' },
    { id: 'related', label: 'Related', source: 'standard' },
    { id: 'activity', label: 'Activity', source: 'standard' },
    { id: 'notes', label: 'Files', source: 'standard' },
  ];
}

/**
 * Get tab source display label
 */
export function getTabSourceLabel(source: 'standard' | 'custom' | 'related'): string {
  const labels: Record<string, string> = {
    standard: 'Standard',
    custom: 'Custom',
    related: 'Related List'
  };
  return labels[source] || source;
}
